import React, { useCallback, useContext } from 'react';

import { Layering } from '@atlaskit/layering/layering';
import { useNotifyOpenLayerObserver } from '@atlaskit/layering/use-notify-open-layer-observer';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import Portal from '@atlaskit/portal/portal';

import PopperWrapper from '../popper-wrapper';
import {
	type ContentProps,
	type DialogContentProps,
	type PopupProps as LegacyPopupProps,
	type TInitialFocusRole,
} from '../types';
import { usePopupAppearance } from '../use-appearance';
import { IdContext } from './id-context';
import { IsOpenContext } from './is-open-context';
import { PopupContentTopLayer } from './popup-content-top-layer';
import { TriggerRefContext } from './trigger-ref-context';
import { useEnsureIsInsidePopup } from './use-ensure-is-inside-popup';

const defaultLayer = 400;
const shouldDisableGpuAccelerationModifiers = [
	{
		name: 'computeStyles',
		options: {
			gpuAcceleration: false,
		},
	},
];

type CommonContentPopupProps = Pick<
	LegacyPopupProps,
	| 'xcss'
	| 'appearance'
	| 'boundary'
	| 'offset'
	| 'onClose'
	| 'testId'
	| 'placement'
	| 'fallbackPlacements'
	| 'popupComponent'
	| 'shouldFlip'
	| 'rootBoundary'
	| 'autoFocus'
	| 'shouldRenderToParent'
	| 'shouldUseCaptureOnOutsideClick'
	| 'shouldIgnoreCloseEvent'
	| 'shouldDisableFocusLock'
	| 'strategy'
	| 'zIndex'
	| 'shouldFitViewport'
	| 'label'
	| 'titleId'
> & {
	/**
	 * ___Use with caution___
	 *
	 * Disables popper.js GPU acceleration for this popup.
	 * This means only positioning will be used, without any transforms.
	 *
	 * Performance will be degraded if the popup is expected to move.
	 *
	 * This should almost never be used, but is sometimes needed
	 * to resolve layering issues.
	 */
	shouldDisableGpuAcceleration?: boolean;
};

type ShouldFitContainerContentPopupProps = CommonContentPopupProps & {
	shouldFitContainer: true;
	shouldRenderToParent?: true;
	strategy?: 'absolute';
};

type StandardPopupContentProps = CommonContentPopupProps & {
	shouldFitContainer?: false;
};

type TPopupContentBase = ShouldFitContainerContentPopupProps | StandardPopupContentProps;

// The `children` type has been kept the same as the Popup `content` prop for now.
// It could be nice to also support ReactNode e.g. `ReactNode | ((props: ContentProps) => ReactNode)`,
// so that consumers don't need to use a function when they are not using the props that are passed.

/**
 * Props for popup content with any role, or no role. `children` does not get `setInitialFocusRef`.
 */
export type PopupContentProps = TPopupContentBase & {
	/**
	 * Use this to set the accessibility role for the popup.
	 * We strongly recommend using only `menu` or `dialog`.
	 * Must be used along with `label` or `titleId`.
	 *
	 * Only `role="dialog"` and `role="alertdialog"` (as literal types) give `children`
	 * the `setInitialFocusRef` setter.
	 */
	role?: string;
	children: (props: ContentProps) => React.ReactNode;
};

/**
 * Props for popup content with `role="dialog"` or `role="alertdialog"`.
 * `children` gets `setInitialFocusRef`.
 */
export type DialogPopupContentProps = TPopupContentBase & {
	/**
	 * Use this to set the accessibility role for the popup.
	 * Must be used along with `label` or `titleId`.
	 * Required. `undefined` lets a role gated on `platform-dst-top-layer` keep the setter.
	 */
	// TODO: drop `| undefined` at the flag cleanup, in a further major, with a codemod
	// that turns the gated role into `role="dialog"`.
	role: TInitialFocusRole | undefined;
	children: (props: DialogContentProps) => React.ReactNode;
};

/**
 * The `PopupContent` component type. The dialog signature comes first, so only a role
 * that is always `'dialog'` or `'alertdialog'` gets `setInitialFocusRef`.
 */
export interface TPopupContent {
	(props: DialogPopupContentProps): React.ReactNode;
	(props: PopupContentProps): React.ReactNode;
}

type TPopupContentImplProps = DialogPopupContentProps | PopupContentProps;

type TPopupContentLegacyProps = TPopupContentImplProps & {
	isOpen: boolean;
	id: string | undefined;
};

/**
 * Legacy (Popper + Portal) implementation of the compositional PopupContent.
 *
 * Renders when the `platform-dst-top-layer` flag is off. This component owns
 * the open layer observer registration, which on the top-layer path is handled
 * by the `Popover` primitive inside `PopupContentTopLayer` instead.
 */
function PopupContentLegacy({
	xcss,
	appearance: inAppearance = 'default',
	children,
	boundary,
	offset,
	strategy,
	onClose,
	testId,
	rootBoundary = 'viewport',
	shouldFlip = true,
	placement = 'auto',
	fallbackPlacements,
	popupComponent,
	autoFocus = true,
	zIndex = defaultLayer,
	shouldUseCaptureOnOutsideClick = false,
	shouldIgnoreCloseEvent,
	shouldRenderToParent: inShouldRenderToParent,
	shouldDisableFocusLock = false,
	shouldFitContainer,
	shouldFitViewport,
	shouldDisableGpuAcceleration = false,
	role,
	titleId,
	isOpen,
	id,
}: TPopupContentLegacyProps): React.ReactNode {
	const triggerRef = useContext(TriggerRefContext);
	const { appearance, shouldRenderToParent } = usePopupAppearance({
		appearance: inAppearance,
		shouldRenderToParent: inShouldRenderToParent,
	});

	const handleOpenLayerObserverCloseSignal = useCallback(() => {
		onClose?.(null);
	}, [onClose]);

	useNotifyOpenLayerObserver({
		isOpen,
		onClose: handleOpenLayerObserverCloseSignal,
		type: 'popup',
	});

	if (!isOpen) {
		return null;
	}

	const popperWrapper = (
		<Layering isDisabled={false}>
			<PopperWrapper
				xcss={xcss}
				appearance={appearance}
				content={children}
				isOpen={isOpen}
				placement={placement}
				fallbackPlacements={fallbackPlacements}
				boundary={boundary}
				rootBoundary={rootBoundary}
				shouldFlip={shouldFlip}
				offset={offset}
				popupComponent={popupComponent}
				id={id}
				testId={testId}
				onClose={onClose}
				autoFocus={autoFocus}
				shouldFitContainer={shouldFitContainer}
				shouldUseCaptureOnOutsideClick={shouldUseCaptureOnOutsideClick}
				shouldIgnoreCloseEvent={shouldIgnoreCloseEvent}
				shouldRenderToParent={shouldRenderToParent}
				shouldDisableFocusLock={shouldDisableFocusLock}
				triggerRef={triggerRef}
				strategy={strategy}
				shouldFitViewport={shouldFitViewport}
				modifiers={shouldDisableGpuAcceleration ? shouldDisableGpuAccelerationModifiers : undefined}
				role={role}
				titleId={titleId}
			/>
		</Layering>
	);

	if (shouldRenderToParent) {
		return popperWrapper;
	}

	return <Portal zIndex={zIndex}>{popperWrapper}</Portal>;
}

/**
 * __Popup content__
 *
 * Popup content is the component that renders the content of the popup.
 *
 * It must be a child of the Popup component. Only `role="dialog"` and
 * `role="alertdialog"` give `children` the `setInitialFocusRef` setter.
 */
function PopupContent(props: DialogPopupContentProps): React.ReactNode;
function PopupContent(props: PopupContentProps): React.ReactNode;
function PopupContent(props: TPopupContentImplProps): React.ReactNode {
	useEnsureIsInsidePopup();
	const isOpen = useContext(IsOpenContext);
	const id = useContext(IdContext);

	// Select the rendering implementation at the component boundary rather than
	// gating hooks inside a single component. Each implementation owns its own
	// hooks, so a runtime feature-flag change swaps component types (a clean
	// remount) instead of changing the hook order of a mounted component.
	//
	// Forwarding the full props onto the chosen implementation is intentional:
	// both implementations declare explicit prop types, so the forward is type
	// checked rather than an unbounded API.
	if (fg('platform-dst-top-layer')) {
		// The top-layer Popover registers with the open layer observer itself.
		// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props -- intentional forward to a typed implementation
		return <PopupContentTopLayer {...props} isOpen={isOpen} id={id} />;
	}

	// eslint-disable-next-line @repo/internal/react/no-unsafe-spread-props -- intentional forward to a typed implementation
	return <PopupContentLegacy {...props} isOpen={isOpen} id={id} />;
}

// Not `export function`: the React Compiler's gating mode drops the export of an overloaded function.
export { PopupContent };
